import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, Twitter, ArrowUp, Instagram } from 'lucide-react';

function Footer() {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Randiv01', label: 'GitHub' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/randiv-dulneth-2196a1392/', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://www.instagram.com/randiv_xi', label: 'Instagram' },
    { icon: Mail, href: 'mailto:randiv.dulneth01@gmail.com', label: 'Email' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Skills', path: '/skills' },
    { name: 'Projects', path: '/projects' },
    { name: 'Achievements', path: '/achievements' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <footer className="bg-gray-50 dark:bg-secondary border-t border-gray-200 dark:border-dark-300 relative">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary"></div>

      <div className="container-custom mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex-shrink-0">
                <img 
                  src="/logo.svg" 
                  alt="MrRandiv Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold font-heading gradient-text mb-1">
                  MrRandiv
                </h3>
                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">
                  Software Developer | SLIIT
                </p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed max-w-md">
              Undergraduate student at SLIIT specializing in ICT. Passionate about building 
              scalable and innovative solutions using modern web technologies and cloud infrastructure.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300 text-gray-600 dark:text-gray-400 hover:text-primary hover:border-primary transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-primary/20"
                    aria-label={social.label}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-600 dark:text-gray-400 hover:text-primary transition-colors duration-300 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-primary group-hover:w-4 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Get In Touch</h4>
            <div className="space-y-3 text-gray-600 dark:text-gray-400 text-sm">
              <p>Always open to connecting and discussing new opportunities.</p>
              <a 
                href="mailto:randiv.dulneth01@gmail.com" 
                className="text-primary hover:text-accent transition-colors inline-block"
              >
                randiv.dulneth01@gmail.com
              </a>
              <p className="flex items-center gap-2">
                <span>📍</span>
                <span>Kandy, Sri Lanka</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 dark:border-dark-300 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 max-w-6xl mx-auto px-4">
            
            {/* Copyright */}
            <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
              © {currentYear} <span className="font-semibold">MrRandiv</span>. All rights reserved.
            </p>

            {/* Scroll to Top Button */}
            <button
              onClick={scrollToTop}
              className="p-2 bg-primary rounded-lg hover:bg-blue-600 transition-all duration-300 hover:scale-110 group"
              aria-label="Scroll to top"
            >
              <ArrowUp size={20} className="text-white group-hover:translate-y-[-2px] transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
